import { useEffect, useRef, useState } from "react";
import { api, unwrap, type GeocodeHit } from "../../api/client";
import { fr } from "../../i18n/fr";
import type { LatLonPt } from "../../map/RouteMap";

const KIND_ICON: Record<string, string> = { eventPoi: "★", place: "◆", address: "@", street: "≡", commune: "⌂", pluscode: "+" };

/**
 * Destination search in the packs (SW-WS-16, SW-PO-04): POIs of the event when [eventId] is given, named places
 * (shops, town halls, schools... CdC EX-RE-07), addresses, streets, communes and Plus Codes. Same offline
 * geocoder as the device (`core-geocoding`).
 */
export function SearchBox({ near, onPick, onClear, placeholder, eventId }: {
  near: LatLonPt | null; onPick(p: LatLonPt, hit: GeocodeHit): void; onClear?(): void; placeholder?: string; eventId?: string;
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<GeocodeHit[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const last = useRef("");
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const text = q.trim();
    if (text.length < 3) { setHits([]); return; }
    timer.current = setTimeout(async () => {
      last.current = text;
      setBusy(true);
      try {
        const r = unwrap(await api.GET("/geocode", { params: { query: { q: text, near: near ? `${near.lat},${near.lon}` : undefined, eventId } } }));
        // a new search point (near) refreshes the hits without reopening the list of a box left by the operator
        if (last.current === text) { setHits(r.hits); if (document.activeElement === input.current) setOpen(true); }
      } catch { setHits([]); } finally { setBusy(false); }
    }, 300);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [q, near, eventId]);

  return (
    <div className="searchbox" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <input ref={input} type="search" placeholder={placeholder ?? fr.workspace.search} aria-label={placeholder ?? fr.workspace.search} value={q} onChange={(e) => { setQ(e.target.value); if (!e.target.value) onClear?.(); }} onFocus={() => hits.length && setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Enter" && hits[0]) { onPick(hits[0].position, hits[0]); setOpen(false); } if (e.key === "Escape") setOpen(false); }} />
      {open && (
        <ul className="search-hits">
          {busy && hits.length === 0 && <li className="muted">{fr.common.loading}</li>}
          {!busy && hits.length === 0 && q.trim().length >= 3 && <li className="muted">{fr.workspace.noHit}</li>}
          {hits.map((h, i) => (
            <li key={i}><button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(h.position, h); setOpen(false); setQ(h.label); }}>
              <span className={"hit-kind " + h.kind} title={fr.workspace.hitKind[h.kind]}>{KIND_ICON[h.kind] ?? "+"}</span> {h.label}
              {(h.category || h.commune) && <span className="hit-detail"> · {[h.category, h.commune].filter(Boolean).join(" · ")}</span>}
            </button></li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** detail line of a hit: category and commune */
export const hitDetail = (h: GeocodeHit) => [h.category, h.commune].filter(Boolean).join(" · ") || undefined;
